import React from 'react';

import { isDefined, silenceError } from '@nfctron/nexus-core';
import {
	UiButton,
	UiDropdownMenu,
	UiDropdownMenuContent,
	UiDropdownMenuItem,
	UiDropdownMenuTrigger,
	cn,
	languageEnumReact,
} from '@nfctron/nexus-react';

import Router from 'next/router';

import { getLocale } from '@root/config/i18n';

import { APP_I18N_NAMESPACE } from '@root/lib/constants/common';
import { languageEnumTickets } from '@root/lib/constants/language';
import { useTranslate } from '@root/lib/hooks/useTranslation';
import { type Component } from '@root/lib/types/components';

export interface LanguageSelectProps {}

/**
 * LanguageSelect component
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export const LanguageSelect: Component<LanguageSelectProps> = (props) => {
	const { t: commonT, lang: currentLang } = useTranslate(APP_I18N_NAMESPACE.COMMON);
	const currentLangEnum = silenceError(() => languageEnumReact.get(currentLang, 'code'));
	if (!isDefined(currentLangEnum)) return null;

	async function onSelectChange(nextLocale: string) {
		/** get own `asPath` route from router's route and query (since it does weird stuff when the path should be rewritten) */
		const asPathFromRoute = Router.route.replace(/\[.*?\]/g, (match) => Router.query[match.slice(1, -1)] as string);
		await Router.push(
			{
				/** setting `/event/[organizer]/[event]` would fail as it is rewritten to `/event/[organizer]/[event]/cart` */
				pathname: asPathFromRoute,
				query: Router.query,
			},
			asPathFromRoute,
			{
				locale: nextLocale,
				scroll: true,
			},
		);
	}

	return (
		<UiDropdownMenu>
			<UiDropdownMenuTrigger asChild>
				<UiButton variant="subtle" className="size-10 rounded-full px-3" size="unstyled">
					<currentLangEnum.Icon className="size-6" />
				</UiButton>
			</UiDropdownMenuTrigger>
			<UiDropdownMenuContent side="bottom" align="center" className="flex flex-col gap-1">
				{languageEnumTickets.list
					.filter((lang) => lang.isSupported)
					.map((lang) => {
						const isSelected = lang.code === currentLangEnum?.code;
						return (
							<UiDropdownMenuItem
								key={lang.code}
								className={cn(isSelected && 'bg-zinc-100 font-medium', 'text-md rounded-md!')}
								onClick={() => onSelectChange(lang.code)}
							>
								{isDefined(lang.Icon) && <lang.Icon className="mr-2 size-4" />}
								{commonT(`constants.language.${lang.languageId}.text`, {
									lng: getLocale(lang.code),
								})}
							</UiDropdownMenuItem>
						);
					})}
			</UiDropdownMenuContent>
		</UiDropdownMenu>
	);
};

/**
 * LanguageSelect component default props
 * @type {ComponentDisplayName}
 */
LanguageSelect.displayName = 'component/LanguageSelect';
